import dayjs from "dayjs";
import firebase from "firebase/compat/app";
import { useEffect, useState } from "react";
import { AppVersionSettingsDoc, Booking, Campaign, List, MaintenanceSettingsDoc, Pitch, User } from '../../shared/types';
import { isBelowMinSupportedVersion, loadedAppVersion, parseAppVersion } from "../../utils/appVersion";
import { sleep } from "../../utils/core";
import { captureFirebaseErrorForSentry } from "../sentryCapture";
import { firestore, logDatabaseReads } from "./app";

// Primary document (see Firebase Queries comment).
export function useListById(listId: string) {
    const [list, setList] = useState<List>();

    useEffect(() => {
        const listRef = firestore.collection('lists').doc(listId);
        const unsubscribe = listRef.onSnapshot((doc) => {
            const newListData = doc.data() as List | undefined;
            if (newListData) {
                const newListDoc: List = {
                    ...newListData,
                    id: doc.id,
                };
                setList(newListDoc);
            }
        });

        return () => {
            unsubscribe();
        };
    }, [listId]);

    return list;
}

/** Live campaign document. Used on campaign detail and pitching; settings still loads once. */
// Primary document (see Firebase Queries comment).
export function useCampaignById(campaignId?: string) {
    const [campaign, setCampaign] = useState<Campaign>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        setCampaign(undefined);
        setLoading(true);
        setError(null);

        if (!campaignId) {
            setLoading(false);
            return;
        }

        const unsubscribe = firestore.collection("campaigns").doc(campaignId).onSnapshot(
            (doc) => {
                logDatabaseReads("useCampaignById", 1);
                if (!doc.exists) {
                    setCampaign(undefined);
                    setError(new Error(`Campaign not found: ${campaignId}`));
                    setLoading(false);
                    return;
                }
                setCampaign({
                    ...(doc.data() as Campaign),
                    id: doc.id,
                });
                setError(null);
                setLoading(false);
            },
            (err) => {
                captureFirebaseErrorForSentry(err, "useCampaignById");
                setError(err);
                setLoading(false);
            },
        );

        return () => {
            unsubscribe();
        };
    }, [campaignId]);

    return { campaign, loading, error };
}

export function usePitchesByCampaignId(campaignId?: string) {
    const [pitches, setPitches] = useState<Pitch[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        setPitches([]);
        setLoading(true);
        setError(null);

        if (!campaignId) {
            setLoading(false);
            return;
        }

        const pitchesQuery = firestore.collection("pitches").where("campaignId", "==", campaignId);
        const unsubscribe = pitchesQuery.onSnapshot(
            (querySnapshot) => {
                logDatabaseReads("usePitchesByCampaignId", querySnapshot.docs.length);
                setPitches(querySnapshot.docs.map((doc) => ({
                    ...(doc.data() as Pitch),
                    id: doc.id,
                })));
                setLoading(false);
            },
            (err) => {
                captureFirebaseErrorForSentry(err, "usePitchesByCampaignId");
                setError(err);
                setLoading(false);
            }
        );

        return () => {
            unsubscribe();
        };
    }, [campaignId]);

    return { pitches, loading, error };
}

/**
 * Open campaigns plus recently closed ones for the opportunities index.
 * Two live snapshots; silent cap 100 per query. Bookers must not enable this (rules are not filters).
 */
export function useOpportunityIndexCampaigns(enabled: boolean) {
    const [openCampaigns, setOpenCampaigns] = useState<Campaign[]>([]);
    const [closedCampaigns, setClosedCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        setOpenCampaigns([]);
        setClosedCampaigns([]);
        setError(null);

        if (!enabled) {
            setLoading(false);
            return;
        }

        setLoading(true);
        let openReady = false;
        let closedReady = false;
        const settle = () => {
            if (openReady && closedReady) {
                setLoading(false);
            }
        };

        const openQuery = firestore.collection("campaigns")
            .where("pitchingOpen", "==", true)
            .orderBy("pitchingOpenChangedAt", "desc")
            .limit(100);
        const since = dayjs().subtract(30, "day").toDate();
        const closedQuery = firestore.collection("campaigns")
            .where("pitchingOpen", "==", false)
            .where("pitchingOpenChangedAt", ">=", since)
            .orderBy("pitchingOpenChangedAt", "desc")
            .limit(100);

        const unsubOpen = openQuery.onSnapshot(
            (querySnapshot) => {
                logDatabaseReads("useOpportunityIndexCampaigns.open", querySnapshot.docs.length);
                setOpenCampaigns(querySnapshot.docs.map((doc) => ({
                    ...(doc.data() as Campaign),
                    id: doc.id,
                })));
                openReady = true;
                settle();
            },
            (err) => {
                captureFirebaseErrorForSentry(err, "useOpportunityIndexCampaigns.open");
                setError(err);
                openReady = true;
                settle();
            },
        );
        const unsubClosed = closedQuery.onSnapshot(
            (querySnapshot) => {
                logDatabaseReads("useOpportunityIndexCampaigns.closed", querySnapshot.docs.length);
                setClosedCampaigns(querySnapshot.docs.map((doc) => ({
                    ...(doc.data() as Campaign),
                    id: doc.id,
                })));
                closedReady = true;
                settle();
            },
            (err) => {
                captureFirebaseErrorForSentry(err, "useOpportunityIndexCampaigns.closed");
                setError(err);
                closedReady = true;
                settle();
            },
        );

        return () => {
            unsubOpen();
            unsubClosed();
        };
    }, [enabled]);

    return { openCampaigns, closedCampaigns, loading, error };
}

function pitchesFromDocs(docs: firebase.firestore.QueryDocumentSnapshot[]): Pitch[] {
    return docs.map((doc) => ({
        ...(doc.data() as Pitch),
        id: doc.id,
    }));
}

/** This-month pitches for a manager. Month-stats bars read this; do not cap it. */
export function usePitchesByManagerIdThisMonth(managerId?: string) {
    const [pitches, setPitches] = useState<Pitch[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        setPitches([]);
        setError(null);

        if (!managerId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const since = dayjs().startOf("month").toDate();
        const pitchesQuery = firestore.collection("pitches")
            .where("managerId", "==", managerId)
            .where("docMeta.createTime", ">=", since)
            .orderBy("docMeta.createTime", "desc");
        const unsubscribe = pitchesQuery.onSnapshot(
            (querySnapshot) => {
                logDatabaseReads("usePitchesByManagerIdThisMonth", querySnapshot.docs.length);
                setPitches(pitchesFromDocs(querySnapshot.docs));
                setLoading(false);
            },
            (err) => {
                captureFirebaseErrorForSentry(err, "usePitchesByManagerIdThisMonth");
                setError(err);
                setLoading(false);
            },
        );

        return () => {
            unsubscribe();
        };
    }, [managerId]);

    return { pitches, loading, error };
}

/** A manager’s pitches from the start of the month three months ago. No cap; timeframe picker later. */
export function useRecentPitchesByManagerId(managerId?: string) {
    const [pitches, setPitches] = useState<Pitch[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        setPitches([]);
        setError(null);

        if (!managerId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const since = dayjs().subtract(3, "month").startOf("month").toDate();
        const pitchesQuery = firestore.collection("pitches")
            .where("managerId", "==", managerId)
            .where("docMeta.createTime", ">=", since)
            .orderBy("docMeta.createTime", "desc");
        const unsubscribe = pitchesQuery.onSnapshot(
            (querySnapshot) => {
                logDatabaseReads("useRecentPitchesByManagerId", querySnapshot.docs.length);
                setPitches(pitchesFromDocs(querySnapshot.docs));
                setLoading(false);
            },
            (err) => {
                captureFirebaseErrorForSentry(err, "useRecentPitchesByManagerId");
                setError(err);
                setLoading(false);
            },
        );

        return () => {
            unsubscribe();
        };
    }, [managerId]);

    return { pitches, loading, error };
}

export function useCampaignBookings(campaignId?: string) {
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        setBookings([]);
        setLoading(true);
        setError(null);

        if (!campaignId) {
            setLoading(false);
            return;
        }

        const bookingsQuery = firestore.collection("bookings").where("campaignId", "==", campaignId);
        const unsubscribe = bookingsQuery.onSnapshot(
            (querySnapshot) => {
                logDatabaseReads("useCampaignBookings", querySnapshot.docs.length);
                setBookings(querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                } as Booking)));
                setLoading(false);
            },
            (err) => {
                captureFirebaseErrorForSentry(err, "useCampaignBookings");
                setError(err);
                setLoading(false);
            }
        );

        return () => {
            unsubscribe();
        };
    }, [campaignId]);

    return { bookings, loading, error };
}

// Live grid snapshot. Not a persisted collection.
// TJN TO-DO: MAYBE - migrate to getFreshManagers with mutation strategy?
export function useManagers2(apiRef: any) {
    const [managers, setManagers] = useState<User[]>([]); // , setManagers
    const [firstTime, setFirstTime] = useState(true);

    useEffect(() => {
        let unsubscribeManagers = () => { };
        let unsubscribeExternalManagers = () => { };

        const uniqueManagerIds: string[] = [];

        const managersQuery = firestore.collection("users").where("roles.manager", "in", [true, false]);
        const externalManagersQuery = firestore.collection("users").where("roles.externalManager", "in", [true, false]);

        // const newManagers: User[] = [];
        // const newExternalManagers: User[] = [];

        async function getManagers() {
            const managersSnapshot = await managersQuery.get();
            logDatabaseReads("useManagers2 - getManagers", managersSnapshot.docs.length);
            const newManagers = managersSnapshot.docs.map((doc) => {
                return {
                    id: doc.id,
                    ...doc.data()
                }
            }) as User[];

            setManagers(newManagers);
            setFirstTime(false);
        }


        if (firstTime) {
            getManagers();
            setFirstTime(false);
        } else {
            unsubscribeManagers = managersQuery.onSnapshot((querySnapshot) => {
                const newManagers: User[] = [];

                let reads = 0;
                querySnapshot.docChanges().forEach((change) => {
                    if (change.type === "added" || change.type === "modified") {
                        reads++;
                        const newDoc = {
                            id: change.doc.id,
                            ...change.doc.data()
                        };
                        newManagers.push(newDoc);
                        uniqueManagerIds.push(change.doc.id);
                    }
                });
                logDatabaseReads("useManagers2 - onSnapshot", reads);
                updateRows(newManagers);
            });

            unsubscribeExternalManagers = externalManagersQuery.onSnapshot((querySnapshot) => {
                const newManagers: User[] = [];
                querySnapshot.docChanges().forEach((change) => {
                    if (change.type === "added" || change.type === "modified") {
                        const newDoc = {
                            id: change.doc.id,
                            ...change.doc.data()
                        };

                        // Only use do updates for those we're not already following in unsubscribeManagers, otherwise we'll get duplicate row errors
                        if (!uniqueManagerIds.find((id) => id === change.doc.id)) {
                            newManagers.push(newDoc);
                        }
                    }
                });
                updateRows(newManagers);
            });
        }

        function updateRows(newRows: User[]) {
            if (apiRef?.current?.updateRows) {
                apiRef.current?.updateRows(newRows);
            } else {
                console.log("NO apiRef.current.updateRows", apiRef.current);
            }
        }

        return () => {
            unsubscribeManagers();
            unsubscribeExternalManagers();
        };

    }, [apiRef, firstTime]);

    return managers;
}

export function useBookingsByCampaignId(campaignId?: string) {
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setBookings([]);
        setLoading(true);

        if (!campaignId) {
            setLoading(false);
            return;
        }

        const bookingsQuery = firestore.collection("bookings").where("campaignId", "==", campaignId);
        const unsubscribe = bookingsQuery.onSnapshot(
            (querySnapshot) => {
                const newBookings: Booking[] = [];

                querySnapshot.docChanges().forEach((change) => {
                    newBookings.push({
                        id: change.doc.id,
                        ...change.doc.data()
                    });

                    setBookings(JSON.parse(JSON.stringify(newBookings)));
                });

                setLoading(false);
            },
            (err) => {
                captureFirebaseErrorForSentry(err, "useBookingsByCampaignId");
                setLoading(false);
            }
        );

        return () => {
            unsubscribe();
        };

    }, [campaignId]);

    return { bookings, loading };
}

export function useSearchBookings(searchByField = 'campaignId', searchByValue: unknown) {
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        console.log(`searching for bookings where ${searchByField} == ${searchByValue}`);

        setBookings([]);
        setLoading(true);

        const bookingsQuery = firestore.collection("bookings").where(searchByField, "==", searchByValue);
        const unsubscribe = bookingsQuery.onSnapshot(
            (querySnapshot) => {
                const newBookings: Booking[] = [];

                querySnapshot.docChanges().forEach((change) => {
                    newBookings.push({
                        id: change.doc.id,
                        ...change.doc.data()
                    });

                    setBookings(newBookings);
                });

                setLoading(false);
            },
            (err) => {
                captureFirebaseErrorForSentry(err, "useSearchBookings");
                setLoading(false);
            }
        );

        return () => {
            unsubscribe();
        };

    }, [searchByField, searchByValue]);

    return { bookings, loading };
}

interface UseBookingsProps {
    managerId?: string,
    apiRef?: any,
    limitToManager?: boolean,
    limitToLast3Months?: boolean,
    bookingId?: string,
}



// https://stackoverflow.com/questions/26578167/es6-object-destructuring-default-parameters
export function useBookings({ managerId = "", limitToManager = true, limitToLast3Months = true, apiRef = undefined, bookingId = undefined }: UseBookingsProps = {}) {
    // export function useBookings(managerId: string = "", apiRef: any, limitToManager: boolean = true, limitToLast3Months: boolean = true) {
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [firstTime, setFirstTime] = useState(true);
    const [queryCount, setQueryCount] = useState(0);
    // const [previousQuery, setPreviousQuery] = useState({ limitToManager, limitToLast3Months }); // , queryCount: queryCount

    // https://stackoverflow.com/a/61786423/2972273
    // Weird, but don't add the {} to the arrow function...
    // const debouncedSetBookings = useMemo(() =>
    //     debounce((newBookings: any) => {
    //         console.log("ACTUALLY UPDATING!", newBookings.length);

    //         // console.log(new Date());
    //         // setBookings(newBookings as Booking[]);
    //         apiRef.current.updateRows(newBookings);
    //     }, 100),
    //     [apiRef]
    // );

    useEffect(() => {
        let unsubscribe = () => { };
        // console.log("EFFECT!", limitToManager, managerId, limitToLast3Months, firstTime);

        let bookingsQuery:
            firebase.firestore.CollectionReference<firebase.firestore.DocumentData> |
            firebase.firestore.Query<firebase.firestore.DocumentData>
            = firestore.collection("bookings");

        if (bookingId) {
            bookingsQuery = bookingsQuery.where("id", "==", bookingId);
        } else {
            if (limitToManager) {
                bookingsQuery = bookingsQuery.where("managerIds", "array-contains", managerId);
            }
            if (limitToLast3Months) {
                bookingsQuery = bookingsQuery.where("monthDue2", '>', dayjs().subtract(3, "months").set('date', 1).set('hour', 0).set('minute', 0).set('second', 0).toDate())
            }
        }

        async function getBookings() {
            const bookingsSnapshot = await bookingsQuery.get();
            logDatabaseReads("useBookings - getBookings", bookingsSnapshot.docs.length);
            const newBookings = bookingsSnapshot.docs.map((doc) => {
                return {
                    id: doc.id,
                    ...doc.data()
                }
            }) as Booking[];

            setBookings(newBookings);
            setFirstTime(false);
        }

        if (firstTime || bookingId) {
            getBookings();
        }
        else {
            let snapCount = 0;

            unsubscribe = bookingsQuery.onSnapshot((querySnapshot) => {
                // if we've gotten more than 25 snapshots, and it's earlier than minute 10, delay...
                if (snapCount > 25 && parseInt(dayjs().format('m')) < 5) {
                    // let delayTime = new Date();
                    // console.log("DELAY", 2000, delayTime, querySnapshot.size, `snapCount:`, snapCount);
                    snapCount = 0;
                    unsubscribe();

                    setTimeout(() => {
                        // console.log(new Date(), "This is happening after the delay at", delayTime);
                        snapCount = 0;
                        setQueryCount(queryCount + 1);
                    }, 2000);
                } else {
                    // console.log("Actually writing", new Date(), querySnapshot.size, `snapCount:`, snapCount);
                    const newBookings: Booking[] = [];

                    let reads = 0;

                    querySnapshot.docChanges().forEach((change) => {
                        if (change.type === "added" || change.type === "modified") {
                            reads++;

                            newBookings.push({
                                id: change.doc.id,
                                ...change.doc.data()
                            });
                        }
                    });
                    logDatabaseReads("useBookings - onSnapshot", reads);

                    if (apiRef?.current?.updateRows) {
                        apiRef.current?.updateRows(newBookings);
                    } else {
                        console.log("NO apiRef.current.updateRows");
                    }
                }

                snapCount++;
            });

            // setPreviousQuery({ limitToManager, limitToLast3Months }); // , queryCount: queryCount
        }
        return () => {
            // console.log("UNSUBSCRIBE!");
            unsubscribe();
        };
    }, [apiRef, limitToManager, limitToLast3Months, managerId, queryCount, firstTime, bookingId]);

    return { bookings, firstTime };
}

interface UseBookingsStreamProps {
    managerId?: string,
    creatorTalentIds?: string[],
    fromMonth?: string,
    toMonth?: string,
}

// Booking stream. Not a persisted collection.
export function useBookingsStream({ managerId = "", creatorTalentIds = undefined, fromMonth = undefined, toMonth = undefined }: UseBookingsStreamProps = {}) {
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setBookings([]); // Reset the bookings array
        setLoading(true); // Assume we're loading new records
        let alreadyUnsubscribed = false;

        let bookingsQuery: firebase.firestore.Query<firebase.firestore.DocumentData> = firestore.collection("bookings");
        if (managerId) {
            bookingsQuery = bookingsQuery.where("managerIds", "array-contains", managerId);
        }
        if (creatorTalentIds) {
            // NOTE: there should never be more than 10 in the array; if so, they should be an external manager
            bookingsQuery = bookingsQuery.where("talentId", "in", creatorTalentIds);
        }
        if (fromMonth) {
            // monthDue2 is always on the 15th, so using >= 1st ensures we get the whole month
            const fromDate = new Date(fromMonth.replace("'", "20").replace(' ', ' 1 '));
            bookingsQuery = bookingsQuery.where("monthDue2", '>=', fromDate);
        }
        if (toMonth) {
            // monthDue2 is always on the 15th, so using the <= 27th ensures we get the whole month
            const toDate = new Date(toMonth.replace("'", "20").replace(' ', ' 27 '));
            bookingsQuery = bookingsQuery.where("monthDue2", '<=', toDate);
        }

        let unsubscribe = () => { };
        getBookingsThenStream(bookingsQuery);

        return () => {
            alreadyUnsubscribed = true; // ensure we don't subscribe to the snapshot listener if cleanup has already been run
            unsubscribe(); // run the unsubscribe function, which might be empty or might be from the snapshot listener
        };

        async function getBookingsThenStream(bookingsQuery: firebase.firestore.Query<firebase.firestore.DocumentData>) {
            // 1. Get the bookings with a normal query first -- This always consumes one read per doc
            // There is no definitive way to know if snapshot data is done loading, so we need to do this so we can show a loading state
            const initialNewBookings: Booking[] = [];
            const res = await bookingsQuery.get();

            res.docs.forEach((doc) => {
                initialNewBookings.push({
                    id: doc.id,
                    ...doc.data()
                } as Booking);
            });

            const filterKey = `${managerId}-${fromMonth}-${toMonth}`;
            logDatabaseReads("useBookingsStream - get()", res.docs.length, undefined, filterKey);
            setLoading(false); // we're done loading the initial data
            setBookings(initialNewBookings);

            // 2. Then stream the bookings (assuming we haven't already unsubscribed
            // Note: on page load, doesn't seem to pull cached data from the initial query unless we wait a bit
            // Presumably because, even though the initial query is finished, Firebase hasn't had time to cache the data
            // But subsequent queries when changing params seem to pull from cache, even if we don't wait
            await sleep(1000);
            if (!alreadyUnsubscribed) {
                unsubscribe = bookingsQuery.onSnapshot(async (querySnapshot) => {
                    const streamedNewBookings: Booking[] = [];
                    let reads = 0;
                    let cached = 0;

                    querySnapshot.docChanges().forEach((change) => {
                        !change.doc.metadata.fromCache ? reads++ : cached++;
                    });

                    querySnapshot.docs.forEach((doc) => {
                        streamedNewBookings.push({
                            id: doc.id,
                            ...doc.data()
                        })
                    });

                    // if (import.meta.env.DEV) {
                    //     console.log(`[${managerId}-${fromMonth}-${toMonth}] - fromCache: ${querySnapshot.metadata.fromCache}, querySnapshot.size: ${querySnapshot.size}, reads: ${reads}, cached: ${cached}`);
                    // }

                    reads && logDatabaseReads("useBookingsStream - onSnapshot", reads, undefined, filterKey);
                    cached && logDatabaseReads("useBookingsStream - onSnapshot - cached", cached, undefined, filterKey);
                    setBookings(streamedNewBookings);
                });
            }
        }
    }, [managerId, fromMonth, toMonth, creatorTalentIds]);

    return { bookings, loading };
}

export function useMaintenanceMode(): {
    ready: boolean;
    active: boolean;
    message: string;
    allowSuperAdminDuringMaintenance: boolean;
} {
    const [state, setState] = useState<{
        ready: boolean;
        active: boolean;
        message: string;
        allowSuperAdminDuringMaintenance: boolean;
    }>({
        ready: false,
        active: false,
        message: "",
        allowSuperAdminDuringMaintenance: false,
    });

    useEffect(() => {
        const unsubscribe = firestore
            .collection("system")
            .doc("maintenance")
            .onSnapshot(
                (doc) => {
                    const data = doc.data() as MaintenanceSettingsDoc | undefined;
                    const trimmed = typeof data?.message === "string" ? data.message.trim() : "";
                    setState({
                        ready: true,
                        active: data?.enabled === true,
                        message: trimmed,
                        allowSuperAdminDuringMaintenance:
                            data?.allowSuperAdminDuringMaintenance === true,
                    });
                },
                (err) => {
                    captureFirebaseErrorForSentry(err, "useMaintenanceMode");
                    setState({
                        ready: true,
                        active: false,
                        message: "",
                        allowSuperAdminDuringMaintenance: false,
                    });
                }
            );

        return () => unsubscribe();
    }, []);

    return state;
}

export function useMinSupportedVersion(): {
    ready: boolean;
    belowMinimum: boolean;
    minSupportedVersion: string | null;
} {
    const [state, setState] = useState<{
        ready: boolean;
        belowMinimum: boolean;
        minSupportedVersion: string | null;
    }>({
        ready: import.meta.env.DEV,
        belowMinimum: false,
        minSupportedVersion: null,
    });

    useEffect(() => {
        if (import.meta.env.DEV) return;

        const unsubscribe = firestore
            .collection("system")
            .doc("appVersion")
            .onSnapshot(
                (doc) => {
                    const data = doc.data() as AppVersionSettingsDoc | undefined;
                    const raw =
                        typeof data?.minSupportedVersion === "string"
                            ? data.minSupportedVersion.trim()
                            : "";
                    const minSupported = raw && parseAppVersion(raw) ? raw : null;
                    const belowMinimum = minSupported
                        ? isBelowMinSupportedVersion(loadedAppVersion, minSupported)
                        : false;
                    setState({
                        ready: true,
                        belowMinimum,
                        minSupportedVersion: minSupported,
                    });
                },
                (err) => {
                    captureFirebaseErrorForSentry(err, "useMinSupportedVersion");
                    setState({
                        ready: true,
                        belowMinimum: false,
                        minSupportedVersion: null,
                    });
                }
            );

        return () => unsubscribe();
    }, []);

    return state;
}
